import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Mail, Loader2, ArrowRight, ShieldCheck, ScanLine, Crown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';

/**
 * Desktop handoff page — shown when BOSCO Capture is opened from a desktop
 * or laptop. We refuse to use a webcam (poor for documents) and instead
 * walk the teacher to their phone via QR + email link.
 */
export default function BoscoCaptureDesktopHandoff({ userEmail }) {
  const [sending, setSending] = useState(false);
  const [emailedTo, setEmailedTo] = useState(null);
  const [emailInput, setEmailInput] = useState(userEmail || '');
  const [link, setLink] = useState(null);

  // Lazy: request a fresh link on demand (so the QR doesn't waste a token if the user never scans).
  async function fetchLink({ alsoEmail = false } = {}) {
    setSending(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const origin = (typeof window !== 'undefined' && window.location.origin) || '';
      const r = await fetch(`${API_URL}/api/bosco-capture/handoff/send-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          origin,
          email: alsoEmail ? (emailInput || userEmail || '') : null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail?.message || `Handoff request failed (${r.status})`);
      }
      const data = await r.json();
      const fullLink = data.link?.startsWith('http') ? data.link : `${origin}${data.linkPath}`;
      setLink(fullLink);
      if (alsoEmail) {
        if (data.emailed) {
          setEmailedTo(emailInput || userEmail);
          toast.success(`Link sent to ${emailInput || userEmail}`);
        } else {
          toast.error('Could not email the link. Use the QR code below instead.');
        }
      }
    } catch (e) {
      toast.error(e.message || 'Something went wrong.');
    } finally {
      setSending(false);
    }
  }

  // Auto-load QR link on first render so the QR is visible immediately.
  // (Lazy state: only one fetch unless user explicitly emails.)
  if (link === null && !sending) {
    fetchLink({ alsoEmail: false });
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-50 dark:from-slate-950 dark:via-emerald-950/20 dark:to-slate-950"
      data-testid="bosco-capture-desktop-handoff"
    >
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-600/30 relative">
            <ScanLine className="h-6 w-6" />
            <Crown className="absolute -top-1.5 -right-1.5 h-4 w-4 text-amber-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              BOSCO Capture<sup className="text-xs">™</sup>
            </h1>
            <p className="text-sm text-slate-500">AI Educational Understanding Engine · Beta</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: explanation */}
          <div className="lg:col-span-3 space-y-5">
            <div>
              <h2 className="text-xl font-semibold leading-snug mb-2">
                BOSCO Capture is designed for your phone
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Mobile cameras produce sharper, glare-free document scans than a webcam.
                Continue on your phone to capture textbooks and worksheets at the
                highest possible quality — BOSCO will then understand the material
                and generate a full teaching kit.
              </p>
            </div>

            <div className="space-y-2">
              {[
                ['HD rear camera', 'Captures fine print, diagrams, and equations clearly.'],
                ['Portrait orientation', 'Matches the natural shape of textbook pages.'],
                ['Live AI guidance', 'BOSCO coaches you through lighting, alignment, and glare in real time.'],
                ['Privacy by design', 'Captured media is auto-deleted after processing. Only your generated resources are kept.'],
              ].map(([h, body]) => (
                <div key={h} className="flex items-start gap-3 p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-800">
                  <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-semibold">{h}</div>
                    <div className="text-xs text-slate-500 leading-relaxed">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: QR + email */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 mb-1 font-semibold">
                  Continue on Mobile
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  Scan the QR with your phone camera. The link is signed to your
                  account and expires in 15 minutes.
                </p>

                <div className="bg-white p-4 rounded-lg inline-block border border-slate-100 shadow-inner" data-testid="bosco-capture-qr">
                  {link ? (
                    <QRCodeSVG value={link} size={188} includeMargin={false} />
                  ) : (
                    <div className="h-[188px] w-[188px] flex items-center justify-center text-slate-400">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* Email option */}
              <div className="mt-5 border-t border-slate-200 dark:border-slate-800 pt-4">
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-1">
                  <Mail className="h-3 w-3" /> or email me the link
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="you@school.com"
                    className="flex-1 text-sm"
                    data-testid="bosco-capture-handoff-email-input"
                  />
                  <Button
                    onClick={() => fetchLink({ alsoEmail: true })}
                    disabled={sending || !emailInput}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bosco-capture-handoff-email-send"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  </Button>
                </div>
                {emailedTo && (
                  <p className="text-[11px] text-emerald-600 mt-2 flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Link sent to {emailedTo}
                  </p>
                )}
              </div>

              <div className="mt-4 flex items-center justify-center gap-1.5 text-[10px] text-slate-400">
                <Smartphone className="h-3 w-3" />
                Optimised for Android & iPhone
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
